import type { AppState } from '../core/types';
import { createMasterClassification } from '../domain/classification';
import { defaultSecondaryConfig, SECONDARY_NO_VALUE, SECONDARY_WIDTH } from '../domain/dualClassification';
import { themeTargetIds } from '../domain/themeTarget';
import type { CategorySplitConfig, ClassificationConfig, SecondaryClassificationConfig, SplitPart } from './config';

/**
 * Panel actions of the linked dual classification (V1.8.1). Each returns the next classification, rebuilt by the one
 * engine entry point (createMasterClassification), so colours, aliases and the theme target follow the usual rules.
 * Nothing here touches the Maestro, the Excel or the project fields.
 */

/** Select values of the two parts of the split table (field selects list them next to the real fields). */
export const SPLIT_OPTION: Readonly<Record<SplitPart, string>> = { primary: 'split:primary', secondary: 'split:secondary' };
export const splitPartOf = (value: string): SplitPart | null => value === SPLIT_OPTION.primary ? 'primary' : value === SPLIT_OPTION.secondary ? 'secondary' : null;

/** Select value of a dimension: a split part, or the field id. */
export const optionValueFor = (field: string | null | undefined, splitPart: SplitPart | undefined, split: CategorySplitConfig | undefined): string =>
  field && splitPart && split?.sourceField === field ? SPLIT_OPTION[splitPart] : field ?? '';

/** Rebuilds categories (A and B) over the classification's own theme target. */
export function rebuildClassification(state: AppState, config: ClassificationConfig): ClassificationConfig {
  const layer = state.layers.find(({ id }) => id === config.layerId);
  if (!state.model || !layer || layer.kind !== 'master') return config;
  return createMasterClassification(state.model, { ...config, level: state.entityViewLevel }, themeTargetIds(layer, config.themeTarget), state.operationalFields);
}

const withSecondary = (state: AppState, secondary: SecondaryClassificationConfig | undefined): ClassificationConfig => {
  const { secondary: _previous, ...rest } = state.classificationConfig; void _previous;
  return rebuildClassification(state, secondary ? { ...rest, secondary } : rest);
};

/** «+ Añadir clasificación secundaria»: an empty, enabled definition (the user then picks its field). */
export const addSecondary = (state: AppState): ClassificationConfig =>
  withSecondary(state, state.classificationConfig.secondary ?? defaultSecondaryConfig(null, 'Clasificación secundaria'));

/** Field of B from a select value (a field id or a split part); a new field starts its colours again. */
export function setSecondaryField(state: AppState, value: string, label: string): ClassificationConfig {
  const config = state.classificationConfig; const current = config.secondary ?? defaultSecondaryConfig(null, label);
  const part = splitPartOf(value); const split = config.categorySplit;
  const field = part ? split?.sourceField ?? null : value || null;
  const name = part && split ? (part === 'primary' ? split.primaryName : split.secondaryName) : label || 'Clasificación secundaria';
  const same = current.field === field && (current.splitPart ?? null) === part;
  const { splitPart: _previousPart, ...base } = current; void _previousPart;
  return withSecondary(state, { ...base, ...(part ? { splitPart: part } : {}), field, name, categories: same ? current.categories : [] });
}

export function patchSecondary(state: AppState, patch: Partial<Pick<SecondaryClassificationConfig, 'enabled' | 'line' | 'width' | 'name'>>): ClassificationConfig {
  const current = state.classificationConfig.secondary; if (!current) return state.classificationConfig;
  const width = patch.width === undefined ? current.width : Math.min(SECONDARY_WIDTH.max, Math.max(SECONDARY_WIDTH.min, patch.width));
  return { ...state.classificationConfig, secondary: { ...current, ...patch, width } };
}

/** Visibility of one outline category (a hidden category draws the normal boundary; the entity stays on the map). */
export function setSecondaryCategoryVisible(state: AppState, value: string, visible: boolean, fallback: { label: string; color: string }): ClassificationConfig {
  const current = state.classificationConfig.secondary; if (!current) return state.classificationConfig;
  const exists = current.categories.some((category) => category.value === value);
  const categories = exists ? current.categories.map((category) => category.value === value ? { ...category, visible } : category) : [...current.categories, { value, label: fallback.label, color: fallback.color, visible }];
  return { ...state.classificationConfig, secondary: { ...current, categories } };
}

/** «Quitar»: back to the simple classification exactly; A (and a split table it reads) is not changed. */
export const removeSecondary = (state: AppState): ClassificationConfig => withSecondary(state, undefined);

/** Whether A ↔ B can be swapped: B must be complete and A categorical (outlines are categories, not numeric ranges). */
export const canSwap = (config: ClassificationConfig): boolean => Boolean(config.field && config.mode === 'categorized' && config.secondary?.field);

/**
 * «Intercambiar»: A takes B's field/part and its colours as fills; B takes A's field/part and its colours as outlines;
 * titles follow. Both are rebuilt, so a colour kept by value is the same category on the other axis.
 */
export function swapClassifications(state: AppState): ClassificationConfig | null {
  const config = state.classificationConfig; const secondary = config.secondary;
  if (!secondary || !canSwap(config)) return null;
  const { splitPart: _principalPart, ...rest } = config; void _principalPart;
  const next: ClassificationConfig = {
    ...rest,
    field: secondary.field,
    ...(secondary.splitPart ? { splitPart: secondary.splitPart } : {}),
    name: secondary.name,
    categories: secondary.categories,
    partialExceptions: undefined,
    legend: { ...config.legend, title: secondary.name },
    secondary: {
      ...secondary,
      field: config.field,
      ...(config.splitPart ? { splitPart: config.splitPart } : { splitPart: undefined }),
      name: config.legend.title || config.name,
      categories: config.categories.filter(({ value }) => value !== SECONDARY_NO_VALUE),
    },
  };
  if (!next.secondary!.splitPart) { const { splitPart: _drop, ...plain } = next.secondary!; void _drop; return rebuildClassification(state, { ...next, secondary: plain }); }
  return rebuildClassification(state, next);
}

/**
 * «Aplicar desglose»: A reads part 1 (fill), B reads part 2 (outline) of the same combined field, through the explicit
 * table. A previous B keeps its line and width; colours of kept categories survive by value.
 */
export function applyCategorySplit(state: AppState, split: CategorySplitConfig): ClassificationConfig {
  const config = state.classificationConfig;
  const previous = config.secondary;
  const keepA = config.field === split.sourceField && config.splitPart === 'primary';
  const keepB = previous?.field === split.sourceField && previous.splitPart === 'secondary';
  const secondary: SecondaryClassificationConfig = {
    ...defaultSecondaryConfig(split.sourceField, split.secondaryName, 'secondary'),
    ...(previous ? { line: previous.line, width: previous.width } : {}),
    categories: keepB ? previous!.categories : [],
  };
  return rebuildClassification(state, {
    ...config, field: split.sourceField, splitPart: 'primary', mode: 'categorized', categorySplit: split, name: split.primaryName,
    categories: keepA ? config.categories : [], partialExceptions: keepA ? config.partialExceptions : undefined,
    legend: { ...config.legend, title: split.primaryName, visible: true }, secondary,
  });
}
